/**
 * Main Entry Point
 *
 * Bootstraps the React application with necessary providers.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import App from './App';
import './index.css';
import { AuthProvider } from './services/auth/AuthProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { queryClient } from './lib/queryClient';
import { persistOptions, QUERY_CACHE_STORAGE_KEY } from './lib/queryPersister';
import { TooltipProvider } from './shared/components/Tooltip';
import { AppErrorBoundary } from './components/AppErrorBoundary';

// Initialize i18n (must be imported before App)
import { i18nReady } from './i18n';

// Leaflet CSS imports
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Capacitor initialization (iOS native only)
async function initCapacitor() {
  // Only import and initialize Capacitor on native platforms
  if (typeof window !== 'undefined' && (window as any).Capacitor) {
    const { Capacitor } = await import('@capacitor/core');
    const { SplashScreen } = await import('@capacitor/splash-screen');
    
    if (Capacitor.isNativePlatform()) {
      // Hide splash screen after a short delay
      setTimeout(async () => {
        await SplashScreen.hide();
      }, 500);
    }
  }
}

// Lazy-chunk recovery: when a route-level dynamic import fails AFTER the app
// loaded (same dead-connection class as the bootstrap watchdog in index.html —
// iOS kills the HTTP/2 connection while backgrounded), Vite fires
// 'vite:preloadError'. One guarded reload fetches the chunk on a fresh
// connection instead of surfacing a blank/broken route. Same sessionStorage
// budget as the watchdog, so the two can never combine into a reload loop.
window.addEventListener('vite:preloadError', (event) => {
  const RETRY_KEY = 'zx_boot_retries';
  let retries = 0;
  try {
    retries = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10) || 0;
  } catch {
    /* storage unavailable — fall through to the safe no-reload path */
  }
  if (retries < 2) {
    event.preventDefault(); // suppress the unhandled rejection
    try {
      sessionStorage.setItem(RETRY_KEY, String(retries + 1));
    } catch {
      /* ignore */
    }
    window.location.reload();
  }
  // Budget exhausted: let the error propagate — the AppErrorBoundary shows
  // its recoverable screen rather than reload-looping.
});

// #3452: the detected language's bundle is a lazy chunk now. Wait for it —
// bounded, so a stalled fetch can never hold the whole app (the bootstrap
// watchdog in index.html would otherwise reload at 30s): after the cap the
// app renders in English and switches as soon as the bundle lands.
const I18N_WAIT_CAP_MS = 2500;
const i18nGate = Promise.race([
  i18nReady,
  new Promise<void>((resolve) => setTimeout(resolve, I18N_WAIT_CAP_MS)),
]);

const renderApp = () => ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={() => {

// Initialize Capacitor (only runs on native iOS, no-op on web)
initCapacitor().catch(console.error);
        // Log when cache is restored — DEV only: the size check re-reads the
        // whole blob, and esbuild's `pure` strips only the console.log call,
        // not this getItem, so it would run on every production startup.
        if (import.meta.env.DEV) {
          console.log('✅ Query cache restored from storage');
          try {
            const cache = localStorage.getItem(QUERY_CACHE_STORAGE_KEY);
            if (cache) {
              console.log('📦 Cache size:', Math.round(cache.length / 1024), 'KB');
            }
          } catch {
            /* storage unavailable — nothing to report */
          }
        }
      }}
    >
      {/* Top-level crash guard — turns an uncaught render error (which would
          otherwise unmount the tree to a blank #cacac2 page) into a graceful,
          recoverable screen and surfaces the culprit. */}
      <AppErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            {/* Single TooltipProvider for the whole app — required by Radix Tooltip.
                See src/shared/components/Tooltip/Tooltip.tsx. */}
            <TooltipProvider delayDuration={200}>
              <App />
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </AppErrorBoundary>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);

i18nGate.then(renderApp, renderApp);
